import React, { useContext } from "react";
import { useParams } from "react-router-dom";
import { LightgalleryProvider, LightgalleryItem } from "react-lightgallery";
import "lightgallery.js/dist/css/lightgallery.css";
import TicketReply from "./TicketReply";
import { AuthContext } from "../../../../Context/AuthContextProvider";
import ReopenFeedback from "./ReopenFeedback";
import { AlertCircle } from "lucide-react";

function SingleTicket() {
  const allTickets = useContext(AuthContext).tickets || [];
  const { ticketId } = useParams();
  const { formatDate } = useContext(AuthContext);

  const thisTicket = allTickets.find((ticket) => ticket._id === ticketId);
  if (!thisTicket) {
    return null;
  }

  const statusColors = {
    open: "bg-green-500",
    assigned: "bg-blue-500",
    inprogress: "bg-yellow-500",
    pending: "bg-orange-500",
    closed: "bg-gray-500",
    reopened: "bg-purple-500",
  };

  const ratingColors = {
    1: "bg-red-500",
    2: "bg-orange-500",
    3: "bg-yellow-500",
    4: "bg-green-500",
    5: "bg-blue-500",
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 px-4 py-6 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* ✅ Page Header - styled like EditAccountDetails */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-xl shadow-lg">
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Ticket Details</h1>
            <p className="text-sm text-gray-600 mt-1">
              View full ticket history, responses and attachments
            </p>
          </div>
        </div>

        {/* ✅ Ticket Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">
                Ticket ID: #{thisTicket._id}
              </h2>

              <div className="flex gap-3">
                {thisTicket.feedbackSummary && (
                  <span
                    className={`px-4 py-1 rounded-full text-white text-sm font-semibold ${
                      ratingColors[thisTicket.feedbackSummary?.rating] ||
                      "bg-gray-400"
                    }`}
                  >
                    {thisTicket.feedbackSummary.rating} ⭐
                  </span>
                )}
                <span
                  className={`px-4 py-1 rounded-full text-white text-sm font-semibold ${
                    statusColors[thisTicket.status] || "bg-gray-500"
                  }`}
                >
                  {thisTicket.status}
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Ticket Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
                <p className="text-sm font-semibold text-blue-900">
                  Created On
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  {formatDate(thisTicket.createdDate)}
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 p-4 rounded-xl">
                <p className="text-sm font-semibold text-green-900">
                  Expected Resolution
                </p>
                <p className="text-sm text-green-700 mt-1">
                  {thisTicket.expectedResolutionDate
                    ? formatDate(thisTicket.expectedResolutionDate)
                    : "Unavailable"}
                </p>
              </div>

              {thisTicket.assignmentHistory?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl col-span-2">
                  <p className="text-sm font-semibold text-yellow-900">
                    Handler Assigned
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    <strong>ID:</strong>{" "}
                    {thisTicket.assignmentHistory[0]?.assignedTo?.AdminID ||
                      "N/A"}
                    <br />
                    <strong>Name:</strong>{" "}
                    {thisTicket.assignmentHistory[0]?.assignedTo?.AdminName ||
                      "Not Available"}
                  </p>
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
              <h3 className="font-bold text-gray-800">Subject</h3>
              <p className="text-gray-700 mt-1">{thisTicket.title}</p>
            </div>

            {/* Message */}
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
              <h3 className="font-bold text-gray-800">Message</h3>
              <p className="text-gray-700 mt-1">
                {thisTicket.description || "Not Available"}
              </p>
            </div>

            {/* Attachments */}
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
              <h3 className="font-bold text-gray-800">Attachments</h3>

              {thisTicket.attachments.length > 0 ? (
                <LightgalleryProvider>
                  <div className="flex gap-4 flex-wrap mt-3">
                    {thisTicket.attachments.map((attachment, i) => (
                      <LightgalleryItem
                        key={i}
                        group="gallery"
                        src={attachment}
                      >
                        <img
                          src={attachment}
                          alt="Attachment"
                          className="rounded-xl shadow-md w-32 h-32 object-cover cursor-pointer hover:scale-105 transition"
                        />
                      </LightgalleryItem>
                    ))}
                  </div>
                </LightgalleryProvider>
              ) : (
                <p className="text-gray-600 text-sm mt-2">
                  No attachments available
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ✅ Replies Section */}
        {thisTicket.replies?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Responses</h2>
            </div>
            <div className="p-6 space-y-4">
              {thisTicket.replies.map((reply) => (
                <div
                  key={reply.id}
                  className="border border-gray-200 bg-white rounded-xl p-4 shadow-sm"
                >
                  <p className="text-sm text-gray-700">
                    Updated by <strong>{reply.givenBy?.name}</strong> on{" "}
                    <strong>{formatDate(reply.updateDate)}</strong>
                  </p>
                  <p className="mt-2 bg-gray-50 p-3 rounded-lg text-gray-900 font-medium">
                    {reply.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ✅ Reopen Summary */}
        {thisTicket.reOpenSummary?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Reopen Summary
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {thisTicket.reOpenSummary.map((r) => (
                <div
                  key={r.id}
                  className="border border-gray-200 bg-white rounded-xl p-4 shadow-sm"
                >
                  <p className="text-sm text-gray-700">
                    Reopened on <strong>{formatDate(r.providedDate)}</strong>
                  </p>
                  <p className="mt-2 bg-gray-50 p-3 rounded-lg text-gray-900 font-medium">
                    {r.comments}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ✅ Feedback */}
        {thisTicket.feedbackSummary && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Feedback Summary
              </h2>
            </div>
            <div className="p-6">
              <p className="text-sm font-medium text-gray-700">
                Feedback provided on{" "}
                <strong>
                  {formatDate(thisTicket.feedbackSummary.providedDate)}
                </strong>
              </p>
              <p className="mt-2 bg-gray-50 p-3 rounded-lg text-gray-900 font-medium">
                {thisTicket.feedbackSummary.feedback}
              </p>
            </div>
          </div>
        )}

        {/* ✅ Reply or Re-open card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {thisTicket.status === "closed" ? (
            thisTicket.feedbackSummary ? null : (
              <ReopenFeedback ticket={thisTicket} />
            )
          ) : (
            <TicketReply ticket={thisTicket} />
          )}
        </div>
      </div>
    </div>
  );
}

export default SingleTicket;
